import { useEffect, useRef } from 'react';

interface ConfirmModalProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** When true, the confirm button is styled as a destructive action. */
  danger?: boolean;
  /** Disable buttons (e.g. while the confirm callback is awaiting). */
  busy?: boolean;
}

/**
 * Minimal confirm dialog. Renders nothing when `open` is false — no need
 * for a portal since Tailwind `fixed` + high z-index covers our layout.
 *
 * - Backdrop click cancels.
 * - Escape key cancels.
 * - Confirm button is focused on open for keyboard-first flows.
 */
export default function ConfirmModal({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  danger,
  busy,
}: ConfirmModalProps) {
  const confirmButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    confirmButtonRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) {
        onCancel();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel, busy]);

  if (!open) return null;

  const confirmClasses = danger
    ? 'bg-red-600 text-white hover:bg-red-500'
    : 'bg-slate-800 text-white hover:bg-slate-700';

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      data-testid="confirm-modal"
    >
      <div
        className="absolute inset-0 bg-slate-900/50"
        data-testid="confirm-modal-backdrop"
        onClick={() => {
          if (!busy) onCancel();
        }}
      />
      <div className="relative bg-white rounded-lg shadow-lg max-w-md w-full p-5 space-y-4">
        <div>
          <h2
            id="confirm-modal-title"
            className="text-base font-semibold text-slate-900"
            data-testid="confirm-modal-title"
          >
            {title}
          </h2>
          {description && (
            <p
              className="text-sm text-slate-600 mt-1"
              data-testid="confirm-modal-description"
            >
              {description}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid="confirm-modal-cancel"
            className="text-sm border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmButtonRef}
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid="confirm-modal-confirm"
            className={`text-sm rounded px-3 py-1.5 disabled:opacity-50 ${confirmClasses}`}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
